<template>
    <div>
        <app-template>

            <!-- ── Header ──────────────────────────────────────────────────── -->
            <div class="flex items-start justify-between mb-6">
                <div>
                    <h1 class="text-2xl font-bold text-gray-900 tracking-tight">Demandes de réservation</h1>
                    <p class="text-sm text-gray-500 mt-1 max-w-lg leading-relaxed">
                        Ces demandes sont soumises par vos clients via votre formulaire en ligne. Traitez-les en proposant des créneaux ou en planifiant directement l'intervention.
                    </p>
                </div>
                <button
                    @click="getReservations"
                    :disabled="loadingGetReservations"
                    class="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 hover:border-gray-300 transition-all duration-150 disabled:opacity-50 flex-shrink-0 ml-4"
                >
                    <i class="fas fa-rotate-right text-xs" :class="{ 'animate-spin': loadingGetReservations }"></i>
                    Actualiser
                </button>
            </div>

            <!-- ── Filtres statut ──────────────────────────────────────────── -->
            <div class="flex items-center gap-2 mb-6 flex-wrap">
                <button
                    v-for="f in statusFilters"
                    :key="f.value"
                    @click="setFilter(f.value)"
                    :class="[
                        'flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-150 border',
                        activeFilter === f.value
                            ? f.activeClass
                            : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300 hover:text-gray-700'
                    ]"
                >
                    <i :class="f.icon + ' text-xs'"></i>
                    {{ f.label }}
                    <!-- <span
                        v-if="countByStatus(f.value) > 0"
                        class="ml-0.5 text-xs font-bold px-1.5 py-0.5 rounded-full"
                        :class="activeFilter === f.value ? 'bg-white/25 text-white' : 'bg-gray-100 text-gray-500'"
                    >
                        {{ countByStatus(f.value) }}
                    </span> -->
                </button>
            </div>

            <!-- ── Loading skeleton ───────────────────────────────────────── -->
            <div v-if="loadingGetReservations" class="space-y-3">
                <div v-for="i in 5" :key="i" class="bg-white rounded-xl border border-gray-100 p-5">
                    <div class="flex items-center gap-4">
                        <div class="w-10 h-10 rounded-full bg-gray-100"></div>
                        <div class="flex-1 space-y-2">
                            <div class="h-4 bg-gray-100 rounded w-1/3"></div>
                            <div class="h-3 bg-gray-100 rounded w-1/2"></div>
                        </div>
                        <div class="h-6 w-20 bg-gray-100 rounded-full"></div>
                    </div>
                </div>
            </div>

            <!-- ── Empty state ─────────────────────────────────────────────── -->
            <div v-else-if="filteredReservations.length === 0" class="flex flex-col items-center justify-center py-24 text-center">
                <div class="w-16 h-16 rounded-2xl bg-orange-50 flex items-center justify-center mb-4">
                    <i class="fas fa-calendar-xmark text-2xl text-orange-400"></i>
                </div>
                <p class="text-gray-900 font-semibold text-lg">Aucune demande</p>
                <p class="text-gray-400 text-sm mt-1">Aucune demande pour ce filtre.</p>
            </div>

            <!-- ── Table ───────────────────────────────────────────────────── -->
            <div v-else class="bg-white rounded-xl border border-gray-100 overflow-hidden shadow-sm">
                <table class="w-full text-sm">
                    <thead>
                        <tr class="border-b border-gray-100 bg-gray-50">
                            <th class="text-left text-xs font-semibold text-gray-400 uppercase tracking-wider px-5 py-3">Client</th>
                            <th class="text-left text-xs font-semibold text-gray-400 uppercase tracking-wider px-5 py-3 hidden md:table-cell">Intervention</th>
                            <th class="text-left text-xs font-semibold text-gray-400 uppercase tracking-wider px-5 py-3 hidden lg:table-cell">Adresse</th>
                            <th class="text-left text-xs font-semibold text-gray-400 uppercase tracking-wider px-5 py-3 hidden sm:table-cell">Reçue le</th>
                            <th class="text-left text-xs font-semibold text-gray-400 uppercase tracking-wider px-5 py-3">Statut</th>
                            <th class="px-5 py-3"></th>
                        </tr>
                    </thead>
                    <tbody class="divide-y divide-gray-50">
                        <tr
                            v-for="reservation in filteredReservations"
                            :key="reservation.id"
                            class="hover:bg-orange-50/40 transition-colors duration-100 cursor-pointer group"
                            @click="openReservation(reservation)"
                        >
                            <td class="px-5 py-4">
                                <div class="flex items-center gap-3">
                                    <div class="w-9 h-9 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                                        <span class="text-orange-600 font-bold text-xs">{{ initials(reservation) }}</span>
                                    </div>
                                    <div>
                                        <p class="font-semibold text-gray-900">{{ reservation.client_prenom }} {{ reservation.client_nom }}</p>
                                        <p class="text-gray-400 text-xs">{{ reservation.client_telephone }}</p>
                                    </div>
                                </div>
                            </td>
                            <td class="px-5 py-4 hidden md:table-cell">
                                <span class="inline-flex items-center gap-1.5 text-gray-700">
                                    <i class="fas fa-fire-flame-curved text-orange-400 text-xs"></i>
                                    {{ reservation.intervention_type ? reservation.intervention_type.nom : '—' }}
                                </span>
                            </td>
                            <td class="px-5 py-4 hidden lg:table-cell text-gray-500">
                                {{ reservation.adresse1 }}, {{ reservation.ville }}
                            </td>
                            <td class="px-5 py-4 hidden sm:table-cell text-gray-500 whitespace-nowrap">
                                {{ formatDate(reservation.created_at) }}
                            </td>
                            <td class="px-5 py-4">
                                <span :class="statusClass(reservation.statut)">
                                    <i :class="statusIcon(reservation.statut)" class="mr-1 text-xs"></i>
                                    {{ statusLabel(reservation.statut) }}
                                </span>
                            </td>
                            <td class="px-5 py-4 text-right">
                                <button
                                    class="opacity-0 group-hover:opacity-100 transition-opacity duration-150 w-8 h-8 rounded-lg bg-orange-500 text-white flex items-center justify-center ml-auto hover:bg-orange-600"
                                    @click.stop="openReservation(reservation)"
                                >
                                    <i class="fas fa-arrow-right text-xs"></i>
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- ══════════════════════════════════════════════════════════════ -->
            <!-- MODAL                                                          -->
            <!-- ══════════════════════════════════════════════════════════════ -->
            <modal name="reservation-detail" width="90%" :max-width="950" :height="'auto'" :scrollable="true" :adaptive="true" :clickToClose="!loadingAccept && !loadingRefuse">
                <div v-if="selectedReservation" class="p-0 overflow-hidden">

                    <!-- ════════════════════════════════════════════════════ -->
                    <!-- STEP 1 : détail                                      -->
                    <!-- ════════════════════════════════════════════════════ -->
                    <template v-if="modalStep === 'detail'">

                        <!-- Header -->
                        <div class="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-white">
                            <div class="flex items-center gap-3">
                                <div class="w-10 h-10 rounded-full bg-orange-500 flex items-center justify-center flex-shrink-0">
                                    <span class="text-white font-bold text-sm">{{ initials(selectedReservation) }}</span>
                                </div>
                                <div>
                                    <p class="font-bold text-gray-900 text-base">{{ selectedReservation.client_prenom }} {{ selectedReservation.client_nom }}</p>
                                    <p class="text-xs text-gray-400">Demande reçue le {{ formatDate(selectedReservation.created_at) }}</p>
                                </div>
                            </div>
                            <div class="flex items-center gap-3">
                                <span :class="statusClass(selectedReservation.statut)">
                                    <i :class="statusIcon(selectedReservation.statut)" class="mr-1.5"></i>
                                    {{ statusLabel(selectedReservation.statut) }}
                                </span>
                                <button @click="closeModal" class="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-all">
                                    <i class="fas fa-xmark text-sm"></i>
                                </button>
                            </div>
                        </div>

                        <!-- Body 2 colonnes -->
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-0" style="max-height: 75vh; overflow-y: auto;">

                            <!-- Carte Mapbox -->
                            <div class="relative bg-gray-100" style="min-height: 420px;">
                                <div id="reservation-map" class="absolute inset-0 w-full h-full"></div>
                                <div class="absolute bottom-16 left-3 right-3 bg-white rounded-xl px-4 py-3 shadow-md border border-gray-200">
                                    <p class="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">
                                        <i class="fas fa-location-dot text-orange-500 mr-1"></i>Adresse d'intervention
                                    </p>
                                    <p class="font-bold text-gray-900 text-sm leading-tight">{{ selectedReservation.adresse1 }}</p>
                                    <p class="text-gray-600 text-xs mt-0.5">{{ selectedReservation.code_postal }} {{ selectedReservation.ville }}</p>
                                </div>
                            </div>

                            <!-- Infos + actions -->
                            <div class="flex flex-col bg-white">
                                <div class="flex-1 px-6 py-5 space-y-4 overflow-y-auto">

                                    <!-- Contact -->
                                    <div>
                                        <p class="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                                            <i class="fas fa-user mr-1.5"></i>Contact
                                        </p>
                                        <p class="font-bold text-gray-900 text-base mb-2">{{ selectedReservation.client_prenom }} {{ selectedReservation.client_nom }}</p>
                                        <div class="flex flex-col gap-2">
                                            <a :href="'tel:' + selectedReservation.client_telephone" class="flex items-center gap-3 p-3 rounded-xl bg-gray-50 hover:bg-orange-50 text-gray-800 hover:text-orange-600 transition-all group">
                                                <div class="w-8 h-8 rounded-lg bg-white border border-gray-200 group-hover:border-orange-200 flex items-center justify-center flex-shrink-0 shadow-sm">
                                                    <i class="fas fa-phone text-orange-500 text-xs"></i>
                                                </div>
                                                <span class="text-sm font-semibold">{{ selectedReservation.client_telephone }}</span>
                                            </a>
                                            <a v-if="selectedReservation.client_email" :href="'mailto:' + selectedReservation.client_email" class="flex items-center gap-3 p-3 rounded-xl bg-gray-50 hover:bg-orange-50 text-gray-800 hover:text-orange-600 transition-all group">
                                                <div class="w-8 h-8 rounded-lg bg-white border border-gray-200 group-hover:border-orange-200 flex items-center justify-center flex-shrink-0 shadow-sm">
                                                    <i class="fas fa-envelope text-orange-500 text-xs"></i>
                                                </div>
                                                <span class="text-sm font-medium truncate">{{ selectedReservation.client_email }}</span>
                                            </a>
                                            <div v-else class="flex items-center gap-3 p-3 rounded-xl bg-yellow-50 border border-yellow-100">
                                                <i class="fas fa-triangle-exclamation text-yellow-500 text-xs flex-shrink-0"></i>
                                                <span class="text-xs text-yellow-700">Aucune adresse e-mail enregistrée</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div class="border-t border-gray-100"></div>

                                    <!-- Intervention -->
                                    <div v-if="selectedReservation.intervention_type">
                                        <p class="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                                            <i class="fas fa-wrench mr-1.5"></i>Intervention demandée
                                        </p>
                                        <p class="font-bold text-gray-900 text-sm mb-2">{{ selectedReservation.intervention_type.nom }}</p>
                                        <span class="inline-flex items-center gap-1.5 text-xs font-semibold text-orange-700 bg-orange-100 px-3 py-1.5 rounded-lg">
                                            <i class="fas fa-clock text-orange-500"></i>
                                            Estimation : {{ selectedReservation.intervention_type.time_min }} min
                                        </span>
                                    </div>

                                    <!-- Notes client -->
                                    <div v-if="selectedReservation.notes">
                                        <div class="border-t border-gray-100 mb-4"></div>
                                        <p class="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                                            <i class="fas fa-comment mr-1.5"></i>Message du client
                                        </p>
                                        <p class="text-gray-700 text-sm leading-relaxed bg-gray-50 rounded-xl px-4 py-3">{{ selectedReservation.notes }}</p>
                                    </div>

                                    <!-- ── Bloc contextuel selon statut ── -->

                                    <!-- PROPOSITIONS ENVOYÉES : résumé des créneaux proposés -->
                                    <div v-if="selectedReservation.statut === 'propositions_envoyees'">
                                        <div class="border-t border-gray-100 mb-4"></div>
                                        <p class="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                                            <i class="fas fa-paper-plane mr-1.5"></i>Créneaux proposés
                                        </p>
                                        <div class="space-y-2">
                                            <div
                                                v-for="(prop, i) in parsedPropositions"
                                                :key="i"
                                                class="flex items-center gap-3 bg-blue-50 border border-blue-100 rounded-xl px-4 py-2.5"
                                            >
                                                <i class="fas fa-calendar-day text-blue-400 text-xs flex-shrink-0"></i>
                                                <div>
                                                    <p class="text-sm font-semibold text-gray-900 capitalize">{{ formatDateLong(prop.date) }}</p>
                                                    <p class="text-xs text-blue-600">{{ prop.slot === 'morning' ? 'Matin' : 'Après-midi' }}</p>
                                                </div>
                                            </div>
                                        </div>
                                        <p class="text-xs text-gray-400 mt-3">
                                            <i class="fas fa-clock mr-1"></i>En attente de la réponse du client.
                                        </p>
                                    </div>

                                    <!-- AUCUN CRÉNEAU : message laissé par le client -->
                                    <div v-if="selectedReservation.statut === 'aucun_creneau'">
                                        <div class="border-t border-gray-100 mb-4"></div>
                                        <p class="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                                            <i class="fas fa-comment-slash mr-1.5"></i>Aucun créneau ne convenait
                                        </p>
                                        <div v-if="selectedReservation.notes_client" class="bg-yellow-50 border border-yellow-100 rounded-xl px-4 py-3">
                                            <p class="text-sm text-gray-700 leading-relaxed">{{ selectedReservation.notes_client }}</p>
                                        </div>
                                        <p v-else class="text-xs text-gray-400 italic">Le client n'a pas laissé de message complémentaire.</p>
                                    </div>

                                    <!-- ACCEPTÉ : créneau retenu -->
                                    <div v-if="selectedReservation.statut === 'accepte' && selectedReservation.selected_slot">
                                        <div class="border-t border-gray-100 mb-4"></div>
                                        <p class="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                                            <i class="fas fa-calendar-check mr-1.5"></i>Créneau retenu par le client
                                        </p>
                                        <div class="bg-green-50 border border-green-100 rounded-xl px-4 py-3 flex items-center gap-3">
                                            <i class="fas fa-circle-check text-green-500"></i>
                                            <div>
                                                <p class="text-sm font-bold text-gray-900 capitalize">{{ formatDateLong(parsedSelectedSlot.date) }}</p>
                                                <p class="text-xs text-green-600">{{ parsedSelectedSlot.slot === 'morning' ? 'Matin' : 'Après-midi' }}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <!-- ── Footer actions selon statut ── -->
                                <div class="px-6 py-4 border-t border-gray-100 bg-gray-50">

                                    <!-- EN ATTENTE -->
                                    <div v-if="selectedReservation.statut === 'en_attente'" class="flex flex-col gap-2">
                                        <button
                                            @click="goToStep('accept')"
                                            class="w-full py-3 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-bold text-sm transition-all flex items-center justify-center gap-2 shadow-sm"
                                        >
                                            <i class="fas fa-check"></i>
                                            Traiter cette demande
                                        </button>
                                        <button
                                            @click="goToStep('refuse')"
                                            class="w-full py-3 rounded-xl bg-white hover:bg-red-50 border border-gray-200 hover:border-red-200 text-gray-700 hover:text-red-600 font-semibold text-sm transition-all flex items-center justify-center gap-2"
                                        >
                                            <i class="fas fa-ban"></i>
                                            Refuser la demande
                                        </button>
                                    </div>

                                    <!-- PROPOSITIONS ENVOYÉES -->
                                    <div v-else-if="selectedReservation.statut === 'propositions_envoyees'" class="flex flex-col gap-2">
                                        <div class="flex items-center gap-2 text-xs text-blue-600 bg-blue-50 border border-blue-100 rounded-xl px-4 py-2.5 mb-1">
                                            <i class="fas fa-hourglass-half flex-shrink-0"></i>
                                            En attente du choix du client. Un e-mail lui a été envoyé avec les créneaux disponibles.
                                        </div>
                                        <button
                                            @click="goToStep('refuse')"
                                            class="w-full py-3 rounded-xl bg-white hover:bg-red-50 border border-gray-200 hover:border-red-200 text-gray-700 hover:text-red-600 font-semibold text-sm transition-all flex items-center justify-center gap-2"
                                        >
                                            <i class="fas fa-ban"></i>
                                            Annuler et refuser la demande
                                        </button>
                                    </div>

                                    <!-- AUCUN CRÉNEAU -->
                                    <div v-else-if="selectedReservation.statut === 'aucun_creneau'" class="flex flex-col gap-2">
                                        <div class="flex items-center gap-2 text-xs text-yellow-700 bg-yellow-50 border border-yellow-100 rounded-xl px-4 py-2.5 mb-1">
                                            <i class="fas fa-phone-volume flex-shrink-0"></i>
                                            Aucun créneau ne convenait au client. Recontactez-le pour trouver une date adaptée.
                                        </div>
                                        <a
                                            :href="'tel:' + selectedReservation.client_telephone"
                                            class="w-full py-3 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-bold text-sm transition-all flex items-center justify-center gap-2 shadow-sm"
                                        >
                                            <i class="fas fa-phone"></i>
                                            Appeler {{ selectedReservation.client_prenom }}
                                        </a>
                                        <button
                                            @click="goToStep('accept')"
                                            class="w-full py-3 rounded-xl bg-white hover:bg-orange-50 border border-gray-200 hover:border-orange-200 text-gray-700 hover:text-orange-600 font-semibold text-sm transition-all flex items-center justify-center gap-2"
                                        >
                                            <i class="fas fa-paper-plane"></i>
                                            Proposer de nouveaux créneaux
                                        </button>
                                        <button
                                            @click="goToStep('refuse')"
                                            class="w-full py-2.5 rounded-xl text-gray-400 hover:text-red-500 font-medium text-sm transition-all flex items-center justify-center gap-2"
                                        >
                                            <i class="fas fa-ban text-xs"></i>
                                            Refuser la demande
                                        </button>
                                    </div>

                                    <!-- ACCEPTÉ -->
                                    <div v-else-if="selectedReservation.statut === 'accepte'" class="flex items-center gap-2 text-sm text-green-700 font-semibold py-1">
                                        <i class="fas fa-circle-check text-green-500"></i>
                                        Rendez-vous confirmé par le client.
                                    </div>

                                    <!-- REFUSÉ -->
                                    <div v-else-if="selectedReservation.statut === 'refuse'" class="flex items-center gap-2 text-sm text-gray-400 py-1">
                                        <i class="fas fa-circle-xmark text-red-400"></i>
                                        Cette demande a été refusée.
                                    </div>

                                </div>
                            </div>
                        </div>
                    </template>

                    <!-- ════════════════════════════════════════════════════ -->
                    <!-- STEP 2a : accepter                                   -->
                    <!-- ════════════════════════════════════════════════════ -->
                    <template v-else-if="modalStep === 'accept'">

                        <!-- Header -->
                        <div class="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-white">
                            <div class="flex items-center gap-3">
                                <button @click="acceptBack" class="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-all">
                                    <i class="fas fa-arrow-left text-sm"></i>
                                </button>
                                <div>
                                    <p class="font-bold text-gray-900 text-base">{{ acceptStepTitle }}</p>
                                    <p class="text-xs text-gray-400">{{ selectedReservation.client_prenom }} {{ selectedReservation.client_nom }} · {{ selectedReservation.intervention_type ? selectedReservation.intervention_type.nom : '' }}</p>
                                </div>
                            </div>
                            <div class="flex items-center gap-3">
                                <div class="hidden sm:flex items-center gap-1.5">
                                    <div v-for="(s, i) in acceptSteps" :key="i"
                                        :class="['h-1.5 rounded-full transition-all duration-300', acceptSubStep === s ? 'w-6 bg-orange-500' : 'w-2 bg-gray-200']"
                                    ></div>
                                </div>
                                <button @click="closeModal" class="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-all">
                                    <i class="fas fa-xmark text-sm"></i>
                                </button>
                            </div>
                        </div>

                        <!-- Sous-étape A : comment traiter ? -->
                        <div v-if="acceptSubStep === 'choix_planification'" class="px-6 py-8" style="min-height: 340px;">
                            <p class="text-2xl font-bold text-gray-700 mb-10 text-center">Comment souhaitez-vous traiter<br />cette demande ?</p>
                            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <button @click="acceptSubStep = 'planifier'" class="flex flex-col items-start gap-3 p-5 rounded-2xl border-2 border-gray-200 hover:border-orange-400 hover:bg-orange-50 text-left transition-all group">
                                    <div class="w-12 h-12 rounded-xl bg-orange-100 group-hover:bg-orange-200 flex items-center justify-center transition-all">
                                        <i class="fas fa-calendar-plus text-orange-500 text-lg"></i>
                                    </div>
                                    <div>
                                        <p class="font-bold text-gray-900 text-sm">Planifier l'intervention</p>
                                        <p class="text-xs text-gray-500 mt-1 leading-relaxed">Vous planifiez directement l'intervention dans votre agenda.</p>
                                    </div>
                                    <span class="text-xs font-semibold text-orange-500 flex items-center gap-1 mt-auto">Choisir <i class="fas fa-arrow-right text-xs"></i></span>
                                </button>
                                <button @click="acceptSubStep = 'choix_type'" class="flex flex-col items-start gap-3 p-5 rounded-2xl border-2 border-gray-200 hover:border-orange-400 hover:bg-orange-50 text-left transition-all group">
                                    <div class="w-12 h-12 rounded-xl bg-orange-100 group-hover:bg-orange-200 flex items-center justify-center transition-all">
                                        <i class="fas fa-paper-plane text-orange-500 text-lg"></i>
                                    </div>
                                    <div>
                                        <p class="font-bold text-gray-900 text-sm">Proposer des créneaux au client</p>
                                        <p class="text-xs text-gray-500 mt-1 leading-relaxed">Vous envoyez des options au client, il choisit ce qui lui convient.</p>
                                    </div>
                                    <span class="text-xs font-semibold text-orange-500 flex items-center gap-1 mt-auto">Choisir <i class="fas fa-arrow-right text-xs"></i></span>
                                </button>
                            </div>
                        </div>

                        <!-- Sous-étape B : planifier -->
                        <div v-else-if="acceptSubStep === 'planifier'" class="px-4 py-4" style="min-height: 400px;">

                            <!-- État succès -->
                            <div v-if="planifierSuccess" class="flex flex-col items-center justify-center py-16 text-center">
                                <div class="w-20 h-20 rounded-full bg-green-50 flex items-center justify-center mb-5">
                                    <i class="fas fa-circle-check text-green-500 text-4xl"></i>
                                </div>
                                <p class="text-xl font-bold text-gray-900 mb-2">Intervention planifiée !</p>
                                <p class="text-sm text-gray-500 leading-relaxed mb-6 max-w-xs">
                                    L'intervention a été créée et assignée avec succès.
                                </p>
                                <div v-if="selectedPlanSlot" class="bg-green-50 border border-green-100 rounded-xl px-5 py-4 text-left w-full max-w-xs">
                                    <p class="text-xs font-semibold text-green-600 uppercase tracking-widest mb-2">Créneau retenu</p>
                                    <p class="font-bold text-gray-900 text-sm capitalize">{{ formatDateLong(selectedPlanSlot.date) }}</p>
                                    <p class="text-xs text-green-600 mt-0.5">
                                        {{ selectedPlanSlot.slot === 'morning' ? 'Demi-journée du matin' : "Demi-journée de l'après-midi" }}
                                    </p>
                                    <p v-if="selectedPlanSlot.techName" class="text-xs text-gray-500 mt-1">
                                        <i class="fas fa-user-hard-hat mr-1"></i>{{ selectedPlanSlot.techName }}
                                    </p>
                                </div>
                            </div>

                            <!-- État chargement API -->
                            <div v-else-if="loadingPlanifier" class="flex flex-col items-center justify-center bg-gradient-to-br from-orange-50 to-amber-50 border border-orange-100 rounded-2xl px-6 py-12 shadow-sm">
                                <div class="relative w-16 h-16 mb-5">
                                    <div class="absolute inset-0 rounded-full border-4 border-orange-100"></div>
                                    <div class="absolute inset-0 rounded-full border-4 border-t-orange-500 animate-spin"></div>
                                    <div class="absolute inset-0 flex items-center justify-center">
                                        <i class="fas fa-calendar-plus text-orange-400 text-lg"></i>
                                    </div>
                                </div>
                                <p class="text-base font-semibold text-orange-700 mb-1">Planification en cours…</p>
                                <p class="text-xs text-orange-500 text-center">Création de l'intervention et assignation du technicien.</p>
                            </div>

                            <!-- Sélection du créneau -->
                            <div v-else>
                                <div class="border rounded-lg bg-white p-3">
                                    <planning-half-day
                                        ref="planSlotPicker"
                                        :days="$account.account.societe.open_days"
                                        :heure-debut-matin="$account.account.societe.heure_ouverture ? $account.account.societe.heure_ouverture.slice(0, 5) : '08:00'"
                                        heurefin-matin="12:00"
                                        heure-debut-apres-midi="12:01"
                                        :heure-fin-apres-midi="$account.account.societe.heure_fermeture ? $account.account.societe.heure_fermeture.slice(0, 5) : '17:00'"
                                        :multi-slots="true"
                                        @slot-selection-change="onPlanSlotChange"
                                    />
                                </div>
                            </div>
                        </div>

                        <!-- Sous-étape C : quel type ? -->
                        <div v-else-if="acceptSubStep === 'choix_type'" class="px-6 py-8" style="min-height: 340px;">
                            <p class="text-2xl font-bold text-gray-700 mb-10 text-center">De quel type d'intervention s'agit-il ?</p>
                            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <button @click="acceptSubStep = 'chantier'" class="flex flex-col items-start gap-3 p-5 rounded-2xl border-2 border-gray-200 hover:border-orange-400 hover:bg-orange-50 text-left transition-all group">
                                    <div class="w-12 h-12 rounded-xl bg-orange-100 group-hover:bg-orange-200 flex items-center justify-center transition-all">
                                        <i class="fas fa-hard-hat text-orange-500 text-lg"></i>
                                    </div>
                                    <div>
                                        <p class="font-bold text-gray-900 text-sm">Chantier</p>
                                        <p class="text-xs text-gray-500 mt-1 leading-relaxed">Plusieurs passages, plusieurs techniciens. Nécessite une prise de contact.</p>
                                    </div>
                                    <span class="text-xs font-semibold text-orange-500 flex items-center gap-1 mt-auto">Choisir <i class="fas fa-arrow-right text-xs"></i></span>
                                </button>
                                <button @click="acceptSubStep = 'creneaux'" class="flex flex-col items-start gap-3 p-5 rounded-2xl border-2 border-gray-200 hover:border-orange-400 hover:bg-orange-50 text-left transition-all group">
                                    <div class="w-12 h-12 rounded-xl bg-orange-100 group-hover:bg-orange-200 flex items-center justify-center transition-all">
                                        <i class="fas fa-bolt text-orange-500 text-lg"></i>
                                    </div>
                                    <div>
                                        <p class="font-bold text-gray-900 text-sm">Intervention de passage</p>
                                        <p class="text-xs text-gray-500 mt-1 leading-relaxed">Un seul passage, un technicien. Le client choisira un créneau parmi vos propositions.</p>
                                    </div>
                                    <span class="text-xs font-semibold text-orange-500 flex items-center gap-1 mt-auto">Choisir <i class="fas fa-arrow-right text-xs"></i></span>
                                </button>
                            </div>
                        </div>

                        <!-- Sous-étape D : chantier -->
                        <div v-else-if="acceptSubStep === 'chantier'" class="px-6 py-8" style="min-height: 340px;">
                            <div class="max-w-md mx-auto">
                                <div class="flex flex-col items-center text-center mb-8">
                                    <div class="w-16 h-16 rounded-2xl bg-orange-50 flex items-center justify-center mb-4">
                                        <i class="fas fa-phone-volume text-2xl text-orange-400"></i>
                                    </div>
                                    <p class="font-bold text-gray-900 text-lg">Recontacter le client</p>
                                    <p class="text-sm text-gray-500 mt-2 leading-relaxed">Ce type d'intervention nécessite un échange avec le client pour convenir ensemble des dates et de l'organisation du chantier.</p>
                                </div>
                                <div class="bg-orange-50 border border-orange-100 rounded-xl px-5 py-4 flex items-center gap-4">
                                    <div class="w-10 h-10 rounded-full bg-orange-500 flex items-center justify-center flex-shrink-0">
                                        <span class="text-white font-bold text-xs">{{ initials(selectedReservation) }}</span>
                                    </div>
                                    <div>
                                        <p class="font-bold text-gray-900 text-sm">{{ selectedReservation.client_prenom }} {{ selectedReservation.client_nom }}</p>
                                        <a :href="'tel:' + selectedReservation.client_telephone" class="text-orange-500 hover:text-orange-600 text-sm font-semibold flex items-center gap-1.5 mt-0.5">
                                            <i class="fas fa-phone text-xs"></i>{{ selectedReservation.client_telephone }}
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Sous-étape E : créneaux -->
                        <div v-else-if="acceptSubStep === 'creneaux'" class="px-4 py-4" style="min-height: 400px;">
                            <div v-if="!selectedReservation.client_email" class="flex items-start gap-3 bg-red-50 border border-red-100 rounded-xl px-4 py-3 mb-4">
                                <i class="fas fa-triangle-exclamation text-red-400 mt-0.5 flex-shrink-0"></i>
                                <p class="text-xs text-red-700 leading-relaxed">Impossible de proposer des créneaux car le client n'a pas d'adresse e-mail enregistrée. Veuillez le contacter directement.</p>
                            </div>
                            <div v-else-if="loadingSendCreneaux" class="flex flex-col items-center justify-center bg-gradient-to-r from-green-50 to-green-100 border border-green-200 rounded-2xl px-6 py-8 mb-4 shadow-sm">
                                <div class="flex items-center justify-center w-16 h-16 rounded-full bg-white border-4 border-green-100 mb-4 shadow">
                                    <i class="fas fa-paper-plane text-2xl text-green-400 animate-bounce"></i>
                                </div>
                                <p class="text-lg font-semibold text-green-700 mb-2">Envoi en cours…</p>
                                <p class="text-sm text-green-600 text-center">Nous envoyons vos créneaux au client.<br>Merci de patienter quelques secondes.</p>
                                <div class="mt-4">
                                    <i class="fas fa-circle-notch animate-spin text-green-400 text-xl"></i>
                                </div>
                            </div>
                            <div v-else>
                                <div class="flex items-start gap-3 bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 mb-4">
                                    <i class="fas fa-circle-info text-blue-400 mt-0.5 flex-shrink-0"></i>
                                    <p class="text-xs text-blue-700 leading-relaxed">Sélectionnez plusieurs créneaux disponibles. Un e-mail sera envoyé au client avec ces options — il choisira celui qui lui convient le mieux.</p>
                                </div>
                                <div class="flex flex-col lg:grid lg:grid-cols-10 gap-4">
                                    <div class="lg:col-span-7">
                                        <div class="border rounded-lg bg-white p-3">
                                            <planning-half-day
                                                :days="$account.account.societe.open_days"
                                                :heure-debut-matin="$account.account.societe.heure_ouverture ? $account.account.societe.heure_ouverture.slice(0, 5) : '08:00'"
                                                heurefin-matin="12:00"
                                                heure-debut-apres-midi="12:01"
                                                :heure-fin-apres-midi="$account.account.societe.heure_fermeture ? $account.account.societe.heure_fermeture.slice(0, 5) : '17:00'"
                                                :multi-slots="true"
                                                @slot-selection-change="onSlotSelectionChange"
                                            />
                                        </div>
                                    </div>
                                    <div class="lg:col-span-3">
                                        <div class="border rounded-lg bg-white p-3">
                                            <div class="bg-gray-50 rounded-lg p-2 text-center mb-3">
                                                <p class="text-xs text-gray-400 uppercase tracking-wide">Créneaux sélectionnés</p>
                                                <p class="text-2xl font-medium">{{ selectedSlots.length }}</p>
                                            </div>
                                            <div v-if="selectedSlots.length === 0" class="text-center text-sm text-gray-400 border border-dashed border-gray-200 rounded-lg py-6 px-3">
                                                Aucun créneau.<br>Cliquez sur le planning.
                                            </div>
                                            <div class="max-h-[320px] overflow-y-auto space-y-3">
                                                <div v-for="(slots, date) in slotsByDate" :key="date">
                                                    <p class="text-xs font-medium text-gray-400 uppercase tracking-widest mb-1.5">{{ formatDate(date) }}</p>
                                                    <div v-for="slot in slots" :key="slotKey(slot)" class="flex items-center gap-2 mb-1">
                                                        <span :class="slot.slot === 'morning' ? 'bg-blue-50 text-blue-800' : 'bg-amber-50 text-amber-800'" class="text-xs font-medium px-2.5 py-1 rounded-full">
                                                            {{ slot.slot === 'morning' ? 'Matin' : 'Après-midi' }}
                                                        </span>
                                                        <span class="text-xs text-gray-500">{{ slot.techName }}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Footer -->
                        <div class="px-6 py-4 border-t border-gray-100 bg-gray-50 flex gap-3">
                            <template v-if="acceptSubStep === 'chantier'">
                                <button @click="acceptSubStep = 'choix_type'" class="flex-1 py-3 rounded-xl border border-gray-200 text-gray-600 font-semibold text-sm hover:bg-gray-100 transition-all">Retour</button>
                                <button @click="confirmAccept" :disabled="loadingAccept" class="flex-1 py-3 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-bold text-sm transition-all disabled:opacity-50 flex items-center justify-center gap-2 shadow-sm">
                                    <i v-if="loadingAccept" class="fas fa-circle-notch animate-spin"></i>
                                    <i v-else class="fas fa-check"></i>
                                    Confirmer & noter à recontacter
                                </button>
                            </template>
                            <template v-else-if="acceptSubStep === 'creneaux'">
                                <button @click="acceptSubStep = 'choix_type'" class="flex-1 py-3 rounded-xl border border-gray-200 text-gray-600 font-semibold text-sm hover:bg-gray-100 transition-all">Retour</button>
                                <button @click="confirmAcceptCreneaux" :disabled="loadingAccept || !selectedReservation.client_email" class="flex-1 py-3 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-bold text-sm transition-all disabled:opacity-50 flex items-center justify-center gap-2 shadow-sm">
                                    <i v-if="loadingAccept" class="fas fa-circle-notch animate-spin"></i>
                                    <i v-else class="fas fa-paper-plane"></i>
                                    Envoyer les créneaux au client
                                </button>
                            </template>
                            <template v-else-if="acceptSubStep === 'planifier'">
                                <template v-if="planifierSuccess">
                                    <!-- Bouton fermeture après succès -->
                                    <button
                                        @click="closeModal"
                                        class="flex-1 py-3 rounded-xl bg-green-500 hover:bg-green-600 text-white font-bold text-sm transition-all flex items-center justify-center gap-2 shadow-sm"
                                    >
                                        <i class="fas fa-check"></i>
                                        Fermer
                                    </button>
                                </template>
                                <template v-else>
                                    <button
                                        @click="acceptSubStep = 'choix_planification'"
                                        :disabled="loadingPlanifier"
                                        class="flex-1 py-3 rounded-xl border border-gray-200 text-gray-600 font-semibold text-sm hover:bg-gray-100 transition-all disabled:opacity-40"
                                    >
                                        Retour
                                    </button>
                                    <button
                                        @click="confirmPlanifier"
                                        :disabled="selectedPlanSlots.length === 0 || loadingPlanifier"
                                        class="flex-1 py-3 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-bold text-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-sm"
                                    >
                                        <template v-if="loadingPlanifier">
                                            <svg class="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Planification…
                                        </template>
                                        <template v-else>
                                            <i class="fas fa-calendar-plus"></i>
                                            Terminer
                                        </template>
                                    </button>
                                </template>
                            </template>
                            <template v-else>
                                <div class="flex-1"></div>
                            </template>
                        </div>
                    </template>

                    <!-- ════════════════════════════════════════════════════ -->
                    <!-- STEP 2b : refuser                                    -->
                    <!-- ════════════════════════════════════════════════════ -->
                    <template v-else-if="modalStep === 'refuse'">

                        <div class="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-white">
                            <div class="flex items-center gap-3">
                                <button @click="goToStep('detail')" class="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-all">
                                    <i class="fas fa-arrow-left text-sm"></i>
                                </button>
                                <div>
                                    <p class="font-bold text-gray-900 text-base">Refuser la demande</p>
                                    <p class="text-xs text-gray-400">{{ selectedReservation.client_prenom }} {{ selectedReservation.client_nom }}</p>
                                </div>
                            </div>
                            <button @click="closeModal" class="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-all">
                                <i class="fas fa-xmark text-sm"></i>
                            </button>
                        </div>

                        <div class="px-6 py-6 space-y-4" style="min-height: 220px;">
                            <div class="flex items-center justify-between gap-4 p-4 bg-red-50 border border-red-100 rounded-xl">
                                <div class="flex items-start gap-4">
                                    <div class="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                                        <i class="fas fa-ban text-red-500"></i>
                                    </div>
                                    <div>
                                        <p class="font-semibold text-gray-900 text-sm">Vous êtes sur le point de refuser cette demande.</p>
                                        <p class="text-xs text-gray-500 mt-1">
                                            <template v-if="selectedReservation.client_email">Le client sera automatiquement informé par e-mail.</template>
                                            <template v-else>Aucun e-mail associé — le client ne sera pas notifié.</template>
                                        </p>
                                    </div>
                                </div>
                                <button @click="confirmRefuse" :disabled="loadingRefuse" class="flex-shrink-0 px-5 py-2.5 rounded-xl bg-red-500 hover:bg-red-600 text-white font-bold text-sm transition-all disabled:opacity-60 flex items-center gap-2 shadow-sm">
                                    <i v-if="loadingRefuse" class="fas fa-circle-notch animate-spin"></i>
                                    <i v-else class="fas fa-check"></i>
                                    Confirmer
                                </button>
                            </div>
                            <div v-if="!selectedReservation.client_email" class="flex items-start gap-4 p-4 bg-yellow-50 border border-yellow-100 rounded-xl">
                                <i class="fas fa-triangle-exclamation text-yellow-500 mt-0.5 flex-shrink-0"></i>
                                <p class="text-xs text-yellow-700 leading-relaxed">Pensez à contacter ce client par téléphone pour l'informer du refus.</p>
                            </div>
                        </div>

                        <div class="px-6 py-4 border-t border-gray-100 bg-gray-50">
                            <button @click="goToStep('detail')" class="w-full py-3 rounded-xl border border-gray-200 text-gray-600 font-semibold text-sm hover:bg-gray-100 transition-all">Annuler</button>
                        </div>
                    </template>

                </div>
            </modal>
        </app-template>
    </div>
</template>

<script>
import PlanningHalfDay from './../Planning/Components/PlanningHalfDay.vue';

export default {
    name: 'ReservationList',
    components: { PlanningHalfDay },
    data() {
        return {
            reservations: [],
            selectedReservation: null,

            loadingGetReservations: false,
            loadingAccept: false,
            loadingRefuse: false,
            loadingSendCreneaux: false,

            modalStep: 'detail',
            acceptSubStep: 'choix_planification',

            selectedSlots: [],
            manuallyEditedTimes: {},

            _reservationMap: null,

            selectedPlanSlots: [],   // créneau unique sélectionné pour la planification directe
            loadingPlanifier: false,  // loader pendant les appels API create + replanifier
            planifierSuccess: false,  // état de succès post-confirmation

            activeFilter: 'en_attente',
            statusFilters: [
                { value: 'en_attente',            label: 'À traiter',              icon: 'fas fa-hourglass-half',       activeClass: 'bg-orange-500 border-orange-500 text-white' },
                { value: 'propositions_envoyees', label: 'Propositions envoyées',  icon: 'fas fa-paper-plane',          activeClass: 'bg-blue-500 border-blue-500 text-white' },
                { value: 'aucun_creneau',         label: 'Aucun créneau convenable', icon: 'fas fa-calendar-xmark',    activeClass: 'bg-yellow-500 border-yellow-500 text-white' },
                { value: 'accepte',               label: 'Confirmées',             icon: 'fas fa-circle-check',         activeClass: 'bg-green-600 border-green-600 text-white' },
                { value: 'refuse',                label: 'Refusées',               icon: 'fas fa-circle-xmark',         activeClass: 'bg-red-500 border-red-500 text-white' },
            ],
        }
    },
    computed: {
        filteredReservations() {
            return this.reservations.filter(r => (r.statut || 'en_attente') === this.activeFilter);
        },
        acceptSteps() {
            if (this.acceptSubStep === 'planifier') return ['choix_planification', 'planifier'];
            if (['choix_type', 'chantier', 'creneaux'].includes(this.acceptSubStep)) return ['choix_planification', 'choix_type', this.acceptSubStep];
            return ['choix_planification'];
        },
        acceptStepTitle() {
            return {
                choix_planification: 'Traiter la demande',
                planifier:           "Planifier l'intervention",
                choix_type:          "Type d'intervention",
                chantier:            'Chantier — recontacter le client',
                creneaux:            'Proposer des créneaux',
            }[this.acceptSubStep] || 'Traiter la demande';
        },
        slotsByDate() {
            return this.selectedSlots.reduce((groups, slot) => {
                if (!groups[slot.date]) groups[slot.date] = [];
                groups[slot.date].push(slot);
                return groups;
            }, {});
        },
        parsedPropositions() {
            if (!this.selectedReservation?.propositions) return [];
            try {
                return typeof this.selectedReservation.propositions === 'string'
                    ? JSON.parse(this.selectedReservation.propositions)
                    : this.selectedReservation.propositions;
            } catch { return []; }
        },
        parsedSelectedSlot() {
            if (!this.selectedReservation?.selected_slot) return null;
            try {
                return typeof this.selectedReservation.selected_slot === 'string'
                    ? JSON.parse(this.selectedReservation.selected_slot)
                    : this.selectedReservation.selected_slot;
            } catch { return null; }
        },
    },
    methods: {
        async getReservations() {
            this.loadingGetReservations = true;
            try {
                const response = await this.$request.get('/reservations/list', { params: { statut: this.activeFilter } });
                this.reservations = response.data || response;
            } catch (e) {
                console.error('Erreur chargement réservations', e);
            } finally {
                this.loadingGetReservations = false;
            }
        },

        setFilter(value) {
            this.activeFilter = value;
            this.getReservations();
        },

        countByStatus(statut) {
            return this.reservations.filter(r => (r.statut || 'en_attente') === statut).length;
        },

        async confirmAccept() {
            if (!this.selectedReservation) return;
            this.loadingAccept = true;
            try {
                this.updateLocalStatus(this.selectedReservation.id, 'accepte');
                this.$toast.success('Demande acceptée. Le client sera recontacté.');
                this.closeModal();
            } catch (e) {
                this.$toast.error('Une erreur est survenue.');
            } finally {
                this.loadingAccept = false;
            }
        },

        async confirmAcceptCreneaux() {
            if (!this.selectedReservation) return;
            if (this.selectedSlots.length === 0) {
                this.$toast.error('Veuillez sélectionner au moins un créneau.');
                return;
            }
            const creneaux = this.selectedSlots.map(s => ({
                date: s.date, slot: s.slot, tech_id: s.account_id, techName: s.techName,
            }));
            this.loadingSendCreneaux = true;
            try {
                await this.$request.post(`/reservations/${this.selectedReservation.id}/propositions`, {
                    propositions: JSON.stringify(creneaux),
                });
                await this.getReservations();
                this.$toast.success('Créneaux envoyés au client par e-mail.');
                this.$modal.hide('reservation-detail');
            } catch (e) {
                this.$toast.error('Une erreur est survenue.');
            } finally {
                this.loadingSendCreneaux = false;
            }
        },

        async confirmRefuse() {
            if (!this.selectedReservation) return;
            this.loadingRefuse = true;
            try {
                await this.$request.post(`/reservations/${this.selectedReservation.id}/refuse`);
                this.updateLocalStatus(this.selectedReservation.id, 'refuse');
                this.$toast.success('Demande refusée.');
                this.closeModal();
            } catch (e) {
                this.$toast.error('Une erreur est survenue.');
            } finally {
                this.loadingRefuse = false;
            }
        },

        openReservation(reservation) {
            this.selectedReservation = reservation;
            this.modalStep = 'detail';
            this.$modal.show('reservation-detail');
            this.$nextTick(() => this.initReservationMap());
        },

        closeModal() {
            this.$modal.hide('reservation-detail');
            if (this._reservationMap) { this._reservationMap.remove(); this._reservationMap = null; }
            this.selectedReservation = null;
            this.modalStep = 'detail';
            this.acceptSubStep = 'choix_planification';
            this.selectedSlots = [];
            this.selectedPlanSlots = [];
            this.loadingPlanifier = false;
            this.planifierSuccess = false;
            this.manuallyEditedTimes = {};
        },

        goToStep(step) {
            if (step === 'detail') {
                this.modalStep = 'detail';
                this.$nextTick(() => this.initReservationMap());
            } else {
                if (this._reservationMap) { this._reservationMap.remove(); this._reservationMap = null; }
                if (step === 'accept') {
                    this.acceptSubStep = 'choix_planification';
                    this.selectedPlanSlot = null;
                    this.planifierSuccess = false;
                }
                this.modalStep = step;
            }
        },

        acceptBack() {
            const prev = { choix_planification: null, planifier: 'choix_planification', choix_type: 'choix_planification', chantier: 'choix_type', creneaux: 'choix_type' };
            const p = prev[this.acceptSubStep];
            if (p) this.acceptSubStep = p;
            else this.goToStep('detail');
        },

        initReservationMap() {
            const r = this.selectedReservation;
            if (!r?.latitude || !r?.longitude) return;
            window.setTimeout(() => {
                if (this._reservationMap) { this._reservationMap.remove(); this._reservationMap = null; }
                mapboxgl.accessToken = 'pk.eyJ1IjoidmljdG9yYW53IiwiYSI6ImNrajhuam1iOTZ1dnQydnFqbzRldDJrdW8ifQ.VkIKVhh5_Pcp7PxqQ-GCCg';
                this._reservationMap = new mapboxgl.Map({
                    container: 'reservation-map',
                    style: 'mapbox://styles/mapbox/streets-v12',
                    center: [parseFloat(r.longitude), parseFloat(r.latitude)],
                    zoom: 14,
                });
                new mapboxgl.Marker({ color: '#f97316' })
                    .setLngLat([parseFloat(r.longitude), parseFloat(r.latitude)])
                    .addTo(this._reservationMap);
            }, 300);
        },

        updateLocalStatus(id, statut) {
            const idx = this.reservations.findIndex(r => r.id === id);
            if (idx !== -1) this.$set(this.reservations, idx, { ...this.reservations[idx], statut });
            if (this.selectedReservation?.id === id) this.selectedReservation = { ...this.selectedReservation, statut };
        },

        onSlotSelectionChange(newSlots) {
            this.selectedSlots = newSlots;
        },

        onPlanSlotChange(newSlots) {
            this.selectedPlanSlots = newSlots;
        },

        async confirmPlanifier() {
            if (this.selectedPlanSlots.length === 0 || !this.selectedReservation) return;

            this.loadingPlanifier = true;
            try {
                const r = this.selectedReservation;
                const duree = r.intervention_type?.time_min || 60;

                const payload = {
                    // Client
                    client_id: r.client_id || null,
                    client: !r.client_id ? {
                        nom:       r.client_nom,
                        prenom:    r.client_prenom,
                        email:     r.client_email,
                        telephone: r.client_telephone,
                    } : null,

                    // Adresse
                    adresse_id: r.adresse_id || null,
                    adresse: !r.adresse_id ? {
                        adresse1:    r.adresse1,
                        code_postal: r.code_postal,
                        ville:       r.ville,
                        latitude:    r.latitude,
                        longitude:   r.longitude,
                    } : null,

                    // 1 appareil minimal issu de la réservation
                    appareils: [{
                        appareil_id:          null,
                        app_type_id:          r.appareil_type || 'intervention',
                        type:                 r.intervention_type?.key || 'intervention',
                        intervention_type_id: r.intervention_type_id || 0,
                        duree_estimee:        duree,
                    }],

                    // Slots sélectionnés dans le planning
                    slots: this.selectedPlanSlots.map(s => ({
                        date:          s.date,
                        slot:          s.slot,
                        tech_id:       parseInt(s.account_id),
                        duree_estimee: duree,
                    })),
                };

                await this.$request.post('/interventions/planifier-json', payload);

                await this.$request.post('/reservations/' + r.id + '/mark-as-planified', {
                    slot: this.selectedPlanSlots[0],
                });

                this.updateLocalStatus(r.id, 'accepte');
                await this.getReservations();

                this.planifierSuccess = true;
                this.$toast.success('Intervention planifiée avec succès !');
            } catch (e) {
                console.error('Erreur planification', e);
                this.$toast.error(e?.message || 'Une erreur est survenue lors de la planification.');
            } finally {
                this.loadingPlanifier = false;
            }
        },

        slotKey(s) { return `${s.date}__${s.account_id}__${s.slot}`; },
        getSlotTime(slot) {
            const key = this.slotKey(slot);
            return this.manuallyEditedTimes[key] !== undefined ? this.manuallyEditedTimes[key] : slot.time;
        },

        initials(r) {
            return (r.client_prenom || '').charAt(0).toUpperCase() + (r.client_nom || '').charAt(0).toUpperCase();
        },

        formatDate(dateStr) {
            if (!dateStr) return '—';
            return new Date(dateStr).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
        },

        formatDateLong(dateStr) {
            if (!dateStr) return '—';
            return new Date(dateStr + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
        },

        statusLabel(statut) {
            return { en_attente: 'En attente', propositions_envoyees: 'Propositions envoyées', aucun_creneau: 'Aucun créneau convenable', accepte: 'Confirmée', refuse: 'Refusée' }[statut] || 'En attente';
        },

        statusClass(statut) {
            const base = 'inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold';
            return {
                accepte:               `${base} bg-green-100 text-green-700`,
                refuse:                `${base} bg-red-100 text-red-600`,
                aucun_creneau:         `${base} bg-yellow-100 text-yellow-700`,
                propositions_envoyees: `${base} bg-blue-100 text-blue-700`,
            }[statut] || `${base} bg-orange-100 text-orange-600`;
        },

        statusIcon(statut) {
            return { accepte: 'fas fa-circle-check', refuse: 'fas fa-circle-xmark', aucun_creneau: 'fas fa-calendar-xmark', propositions_envoyees: 'fas fa-paper-plane' }[statut] || 'fas fa-clock';
        },
    },

    mounted() {
        this.getReservations();
    },
}
</script>